import { atom, createStore } from 'jotai';
import { atomWithReset, atomWithStorage, createJSONStorage } from 'jotai/utils';

import { SessionData } from '@/modules/api/types';
import { getCookie } from '@/modules/common';
import { BridgeType, ChatFeedbackType, ErrorType, MessageType, PageType, ShopifyCart } from '@/modules/models';
import { PopupState } from '@/modules/models/PopupState';
import { WidgetPopupType } from '@/modules/models/WidgetPopupType';

import {
  CHAT_ID_STORAGE,
  OPEN_CHAT_STORAGE,
  SESSION_FINISH_TIME_STORAGE,
  TOKEN_STORAGE,
  VIEWED_PRODUCTS_STORAGE,
  WIDGET_POPUPS_STATE_STORAGE,
} from './constants';

const atomWithLocalStorage = <T>(key: string, defaultValue: T) =>
  atomWithStorage(key, defaultValue, undefined, { getOnInit: true });

export const atomWithSessionStorage = <T>(key: string, initialValue: T) =>
  atomWithStorage<T>(
    key,
    initialValue,
    createJSONStorage(() => sessionStorage),
    { getOnInit: true }
  );

export const store = createStore();

export const isFontsLoadedAtom = atom(false);

export const isUserAnsweredAtom = atom(false);
export const isMlAnsweredAtom = atom(false);
export const isUnauthorizedAtom = atom(false);
export const requestsQueueAtom = atom<
  {
    url: string;
    method: string;
    headers: HeadersInit;
    body?: string;
  }[]
>([]);

export const messageTextAtom = atom('');
export const isLoadingMessageAtom = atom(false);
export const messagesAtom = atom<MessageType[]>([]);

export const bridgeAtom = atomWithReset<BridgeType>({
  translations: {},
  closeChatWithEsc: true,
  isAlwaysOpenWSConnection: false,
} as BridgeType);

export const cartAtom = atom<{ isOpen: boolean; products: ShopifyCart[] }>({
  isOpen: false,
  products: [],
});

export const messageTimeoutAtom = atom<number>(-1);
export const chatFeedbackAtom = atom<ChatFeedbackType | null>(null);

// Api atoms

export const sessionAtom = atom<SessionData>({
  uid: '',
  token: '',
  countryCode: '',
  isTestUser: false,
  userAgent: '',
  customerId: null,
  localization: '',
  location: null,
});
export const isSessionSentFirstTimeAtom = atom(false);

// Local storage atoms

export const tokenAtom = (() => {
  const localToken = localStorage.getItem(TOKEN_STORAGE);
  let token = localToken ? JSON.parse(localToken) : '';

  const cookieToken = getCookie(TOKEN_STORAGE);

  if (cookieToken && token !== cookieToken) {
    token = cookieToken;
  }

  return atomWithLocalStorage(TOKEN_STORAGE, token);
})();

export const chatIdAtom = atomWithLocalStorage<number | null>(CHAT_ID_STORAGE, null);
export const sessionFinishTimeAtom = atomWithLocalStorage(SESSION_FINISH_TIME_STORAGE, 0);
export const mlVersionAtom = atom<string | null>(null);

export const errorAtom = atom<ErrorType | null>(null);

// Session storage atoms
export const isOpenChatAtom = atomWithStorage(
  OPEN_CHAT_STORAGE,
  false,
  createJSONStorage<boolean>(() => sessionStorage)
);

export const isResetChatAtom = atom<boolean>(false);
export const isReadyForScrollAtom = atom(false);

export const viewedProductsAtom = atomWithSessionStorage<string[]>(VIEWED_PRODUCTS_STORAGE, []);

export const renderedColorsAtom = atom<Record<string, boolean>>({});
export const renderedMessagesAtom = atom<Record<string, boolean>>({});
export const messagesReceivedTimeAtom = atom<Record<string, number>>({});

export type InitState = Omit<
  Record<PageType, PopupState>,
  PageType.CART | PageType.AHAVA_WORDPRESS | PageType.ADD_TO_CART_SUGGESTION
>;
export const widgetPopupsStatesAtom = atomWithSessionStorage<{
  [WidgetPopupType.INIT]: InitState;
  [WidgetPopupType.INIT_WITH_CLOSE_BTN]: PopupState;
  [WidgetPopupType.VIEWED_PRODUCTS]: PopupState;
  [WidgetPopupType.CROSS_SALE]: PopupState;
  [WidgetPopupType.RETURN_VISITOR]: PopupState;
  [WidgetPopupType.STUCK_ON_PAGE]: PopupState;
  [WidgetPopupType.HOVER]: PopupState;
}>(WIDGET_POPUPS_STATE_STORAGE, {
  [WidgetPopupType.INIT]: {
    [PageType.HOME]: PopupState.IDLE,
    [PageType.ABOUT]: PopupState.IDLE,
    [PageType.PRODUCT]: PopupState.IDLE,
    [PageType.DEFAULT]: PopupState.IDLE,
    [PageType.COLLECTION]: PopupState.IDLE,
  },
  [WidgetPopupType.INIT_WITH_CLOSE_BTN]: PopupState.IDLE,
  [WidgetPopupType.VIEWED_PRODUCTS]: PopupState.IDLE,
  [WidgetPopupType.CROSS_SALE]: PopupState.IDLE,
  [WidgetPopupType.RETURN_VISITOR]: PopupState.IDLE,
  [WidgetPopupType.STUCK_ON_PAGE]: PopupState.IDLE,
  [WidgetPopupType.HOVER]: PopupState.IDLE,
});
